import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import {
  Package,
  Wallet,
  AlertTriangle,
  TrendingUp,
  RefreshCw,
  ShoppingCart,
  ArrowRight
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";

const StatCard = ({ icon: Icon, title, value, subtitle, color, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.3, delay }}
  >
    <Card className="border-slate-200 hover:shadow-md transition-shadow">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-medium text-slate-500">{title}</p>
            <p className={`text-3xl font-heading font-bold mt-1 ${color}`}>
              {value}
            </p>
            {subtitle && (
              <p className="text-sm text-slate-500 mt-1">{subtitle}</p>
            )}
          </div>
          <div className={`w-12 h-12 rounded-md flex items-center justify-center ${color.replace('text-', 'bg-').replace('600', '100')}`}>
            <Icon className={`w-6 h-6 ${color}`} strokeWidth={1.5} />
          </div>
        </div>
      </CardContent>
    </Card>
  </motion.div>
);

const ActivityItem = ({ item, type }) => {
  const isRefill = type === "refill";
  const Icon = isRefill ? RefreshCw : ShoppingCart;
  
  return (
    <div className="flex items-center gap-4 py-3 border-b border-slate-100 last:border-0">
      <div className={`w-10 h-10 rounded-md flex items-center justify-center ${isRefill ? 'bg-emerald-100' : 'bg-rose-100'}`}>
        <Icon className={`w-5 h-5 ${isRefill ? 'text-emerald-600' : 'text-rose-600'}`} strokeWidth={1.5} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-slate-900 truncate">
          {item.product_name}
        </p>
        <p className="text-sm text-slate-500">
          {isRefill ? `Fach ${item.slot_id} · +${item.quantity}` : `${item.quantity}x · ${item.total?.toFixed(2)}€`}
        </p>
      </div>
      <span className="text-xs text-slate-400 font-mono">
        {new Date(item.created_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}
      </span>
    </div>
  );
};

const DashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/reports/dashboard`);
      setStats(res.data);
    } catch (err) {
      toast.error("Fehler beim Laden der Statistiken");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-600" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-heading font-bold text-slate-900 tracking-tight">
          Dashboard
        </h1>
        <p className="text-slate-500 mt-1">
          Übersicht über den Snackautomaten
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={Wallet}
          title="Kassenbestand"
          value={`${stats?.cash_balance?.toFixed(2) || '0.00'}€`}
          subtitle={`+${stats?.revenue_today?.toFixed(2) || '0.00'}€ heute`}
          color="text-emerald-600"
          delay={0}
        />
        <StatCard
          icon={Package}
          title="Produkte"
          value={stats?.products_count || 0}
          subtitle={`${stats?.filled_compartments || 0} Fächer belegt`}
          color="text-blue-600"
          delay={0.1}
        />
        <StatCard
          icon={AlertTriangle}
          title="Niedriger Bestand"
          value={stats?.low_stock_count || 0}
          subtitle="Fächer nachfüllen"
          color="text-amber-600"
          delay={0.2}
        />
        <StatCard
          icon={TrendingUp}
          title="Umsatz heute"
          value={`${stats?.revenue_today?.toFixed(2) || '0.00'}€`}
          subtitle={`${stats?.sales_count_today || 0} Verkäufe`}
          color="text-rose-600"
          delay={0.3}
        />
      </div>

      {/* Activity Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Refills */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.4 }}
        >
          <Card className="border-slate-200">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg font-heading">
                Letzte Auffüllungen
              </CardTitle>
              <Link to="/auffuellen">
                <Button variant="ghost" size="sm" className="text-rose-600 hover:text-rose-700">
                  Alle <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {stats?.recent_refills?.length > 0 ? (
                <div>
                  {stats.recent_refills.map((item) => (
                    <ActivityItem key={item.id} item={item} type="refill" />
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-center py-8">
                  Noch keine Auffüllungen
                </p>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Recent Sales */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.5 }}
        >
          <Card className="border-slate-200">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg font-heading">
                Letzte Verkäufe
              </CardTitle>
              <Link to="/berichte">
                <Button variant="ghost" size="sm" className="text-rose-600 hover:text-rose-700">
                  Alle <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {stats?.recent_sales?.length > 0 ? (
                <div>
                  {stats.recent_sales.map((item) => (
                    <ActivityItem key={item.id} item={item} type="sale" />
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-center py-8">
                  Noch keine Verkäufe
                </p>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.6 }}
      >
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-heading">
              Schnellzugriff
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              <Link to="/auffuellen">
                <Button className="bg-rose-600 hover:bg-rose-700 btn-hover" data-testid="quick-refill-btn">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Auffüllen
                </Button>
              </Link>
              <Link to="/automat">
                <Button variant="outline" className="btn-hover" data-testid="quick-automat-btn">
                  Automat anzeigen
                </Button>
              </Link>
              <Link to="/kasse">
                <Button variant="outline" className="btn-hover" data-testid="quick-cash-btn">
                  <Wallet className="w-4 h-4 mr-2" />
                  Kasse
                </Button>
              </Link>
              <Link to="/produkte">
                <Button variant="outline" className="btn-hover" data-testid="quick-products-btn">
                  <Package className="w-4 h-4 mr-2" />
                  Produkte verwalten
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default DashboardPage;
